// Modal para cancelar una venta cerrada: motivo, verificación y condición de lo que regresa.
// Presentacional: el padre monta la mutación y le pasa `pending` y `error`.
import { useId, useMemo, type ReactNode } from 'react';
import { Controller, useForm, useWatch, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Modal, Button, NumberInput } from '@/kit';
import { Banner } from '@/components/ui/Banner';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/api/errors';
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import type { ReturnPieceCondition } from '@/api/services/retail-wizard/retail-wizard.client';
import {
    CANCEL_REASON_MAX,
    buildCancelSaleFormSchema,
    emptyCancelSaleForm,
    returnablePiecesOf,
    toReturnConditions,
    type CancelSaleForm,
    type ReturnPieceRow,
    type ReturnQuantityRow,
    type Translate,
} from './returnConditions';

/** Código del silo → mensaje del modal; `text` es el respaldo si falta la clave de i18n. */
const ERROR_BY_CODE: Record<string, { key: string; text: string }> = {
    CANCEL_WINDOW_EXPIRED: {
        key: 'errores.ventanaVencida',
        text: 'La ventana para cancelar esta venta ya venció. Pide la cancelación por el proceso manual.',
    },
    SALE_ALREADY_CANCELLED: {
        key: 'errores.yaCancelada',
        text: 'Esta venta ya estaba cancelada. Actualiza la lista para ver su estado real.',
    },
    LATE_CANCEL_EXCEPTION_REQUIRED: {
        key: 'errores.fueraDeVentana',
        text: 'Esta venta ya salió de la ventana de cancelación. Para cancelarla, levanta una excepción de cancelación tardía en Excepciones.',
    },
    LATE_CANCEL_EXCEPTION_INVALID: {
        key: 'errores.excepcionInvalida',
        text: 'La excepción de cancelación tardía no autoriza esta venta: debe estar aprobada y ser de esta misma venta. Revísala en Excepciones.',
    },
    SALE_NOT_FOUND: {
        key: 'errores.ventaNoEncontrada',
        text: 'No encontramos esta venta en tu cadena. Actualiza la lista y vuelve a buscarla.',
    },
    IMEI_NOT_RETURNABLE: {
        key: 'errores.equipoNoVerificado',
        text: 'No se verificó el equipo, así que no cancelamos la venta. Verifica el equipo en tienda y vuelve a intentar.',
    },
    RETURN_CONDITION_INVALID: {
        key: 'errores.condicionInvalida',
        text: 'No cancelamos la venta: alguna pieza marcada no es de esta venta. Actualiza la venta y revisa la condición de las piezas.',
    },
    RETURN_QTY_EXCEEDS_SOLD: {
        key: 'errores.unidadesExcedidas',
        text: 'No cancelamos la venta: marcaste más unidades dañadas de las que se vendieron. Corrige la cantidad y vuelve a intentar.',
    },
    CREDIT_HAS_PAYMENTS: {
        key: 'errores.creditoConPagos',
        text: 'No cancelamos la venta: su crédito ya tiene pagos.',
    },
    CREDIT_NOT_CANCELLABLE: {
        key: 'errores.creditoNoCancelable',
        text: 'No cancelamos la venta: su crédito ya no admite la cancelación.',
    },
    CREDIT_CANCEL_FAILED: {
        key: 'errores.creditoNoCancelado',
        text: 'No pudimos cancelar el crédito, así que la venta no se canceló. Inténtalo más tarde.',
    },
    COLLECTION_IN_FLIGHT: {
        key: 'errores.cobroEnCurso',
        text: 'Hay un cobro en curso para este crédito. Inténtalo de nuevo en unos minutos.',
    },
    VALIDATION_ERROR: {
        key: 'errores.datosInvalidos',
        text: 'No cancelamos la venta. Revisa los datos del formulario e intenta de nuevo.',
    },
};

/** Sin teléfono el back lee `equipmentVerified` como «piezas confirmadas». */
const PIECES_NOT_CONFIRMED = {
    key: 'errores.piezasNoConfirmadas',
    text: 'No se confirmaron las piezas, así que no cancelamos la venta. Revisa las piezas que se devuelven y vuelve a intentar.',
};

const GENERIC_ERROR = 'No pudimos cancelar la venta. Revisa tu conexión e intenta de nuevo.';

/** Texto del error: código conocido, luego el `msg` del silo, luego el genérico de red. */
export function cancelErrorMessage(
    error: unknown,
    t: Translate,
    withoutPhone = false,
): string | null {
    if (error === null || error === undefined) return null;
    if (error instanceof ApiError) {
        if (Object.prototype.hasOwnProperty.call(ERROR_BY_CODE, error.code)) {
            const mapped =
                withoutPhone && error.code === 'IMEI_NOT_RETURNABLE'
                    ? PIECES_NOT_CONFIRMED
                    : ERROR_BY_CODE[error.code];
            return t(mapped.key, { defaultValue: mapped.text });
        }
        // `msg` puede llegar undefined en runtime aunque el tipo diga string.
        const fromBackend = typeof error.msg === 'string' ? error.msg.trim() : '';
        if (fromBackend.length > 0) return fromBackend;
    }
    return t('errores.generico', { defaultValue: GENERIC_ERROR });
}

export interface CancelSaleModalProps {
    /** Folio de la venta — se muestra para que el operador confirme que cancela LA venta correcta. */
    folio: string;
    /** Líneas de la venta; de ahí salen las piezas cuya condición se pregunta. */
    lines?: SaleLine[];
    onConfirm: (
        reason: string,
        equipmentVerified: boolean,
        returnConditions: ReturnPieceCondition[],
    ) => void;
    onClose: () => void;
    /** La mutación está corriendo: bloquea el formulario para no mandarla dos veces. */
    pending: boolean;
    /** Error de la última cancelación fallida (lo pasa el padre desde la mutación). */
    error?: unknown;
    /** Nombre de un regalo cuando su línea solo trae el SKU; sin él se muestra el de la línea. */
    giftName?: (line: SaleLine, lineName: string) => ReactNode;
    /** Venta sin teléfono: la casilla confirma las piezas que se devuelven, no el equipo. */
    withoutPhone?: boolean;
}

type GiftName = CancelSaleModalProps['giftName'];

/** Nombre visible de una fila: el del regalo si lo es, o el de la línea. */
function rowName(row: ReturnPieceRow | ReturnQuantityRow, giftName: GiftName): ReactNode {
    return row.giftLine && giftName ? giftName(row.giftLine, row.label) : row.label;
}

const FIELD_LABEL = 'text-sm font-medium text-foreground';
const FIELD_ERROR = 'text-xs text-destructive';

/** Buena o dañada de una pieza con identificador; dañada pide el motivo. */
function PieceConditionRow({
    control,
    index,
    source,
    giftName,
    disabled,
    t,
}: {
    control: Control<CancelSaleForm>;
    index: number;
    source: ReturnPieceRow;
    giftName: GiftName;
    disabled: boolean;
    t: Translate;
}) {
    const reasonId = useId();
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- ruta tipada de react-hook-form
    const path = `pieces.${index}` as const;
    const piece = useWatch({ control, name: path });
    return (
        <li className="space-y-2 py-2.5">
            <Controller
                control={control}
                name={`${path}.condition`}
                render={({ field }) => (
                    <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <legend className="sr-only">
                            {t('cancelar.condicionPieza', {
                                defaultValue: 'Condición de {{label}} {{identifier}}',
                                label: piece.label,
                                identifier: piece.identifier,
                            })}
                        </legend>
                        <div className="min-w-0 flex-1 text-sm">
                            <div className="font-medium">{rowName(source, giftName)}</div>
                            <div className="font-mono text-xs text-muted-foreground">
                                {piece.identifier}
                            </div>
                        </div>
                        {(['GOOD', 'DAMAGED'] as const).map((value) => (
                            <label key={value} className="flex items-center gap-1.5 text-sm">
                                <input
                                    type="radio"
                                    name={field.name}
                                    value={value}
                                    checked={field.value === value}
                                    disabled={disabled}
                                    onChange={() => {
                                        field.onChange(value);
                                    }}
                                />
                                {value === 'GOOD'
                                    ? t('cancelar.buena', { defaultValue: 'Buena' })
                                    : t('cancelar.danada', { defaultValue: 'Dañada' })}
                            </label>
                        ))}
                    </fieldset>
                )}
            />
            {piece.condition === 'DAMAGED' ? (
                <DamageReasonField
                    control={control}
                    name={`${path}.damageReason`}
                    id={reasonId}
                    label={t('cancelar.motivoDano', {
                        defaultValue: 'Motivo del daño de {{name}}',
                        name: piece.identifier,
                    })}
                    disabled={disabled}
                    t={t}
                />
            ) : null}
        </li>
    );
}

/** Cuántas unidades de un SKU por cantidad vienen dañadas (0…vendidas) y por qué. */
function QuantityConditionRow({
    control,
    index,
    source,
    giftName,
    disabled,
    t,
}: {
    control: Control<CancelSaleForm>;
    index: number;
    source: ReturnQuantityRow;
    giftName: GiftName;
    disabled: boolean;
    t: Translate;
}) {
    const qtyId = useId();
    const reasonId = useId();
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- ruta tipada de react-hook-form
    const path = `quantities.${index}` as const;
    const row = useWatch({ control, name: path });
    return (
        <li className="space-y-2 py-2.5">
            <Controller
                control={control}
                name={`${path}.damagedQty`}
                render={({ field, fieldState }) => (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <div className="min-w-0 flex-1 text-sm">
                            <div className="font-medium">{rowName(source, giftName)}</div>
                            <div className="text-xs text-muted-foreground">
                                {source.giftLine
                                    ? t('cancelar.deRegalo', {
                                          defaultValue: '{{soldQty}} de regalo',
                                          soldQty: row.soldQty,
                                      })
                                    : t('cancelar.vendidas', {
                                          defaultValue: '{{soldQty}} vendidas',
                                          soldQty: row.soldQty,
                                      })}
                            </div>
                        </div>
                        <label htmlFor={qtyId} className="text-sm">
                            {t('cancelar.danadas', { defaultValue: 'Dañadas' })}
                        </label>
                        <NumberInput
                            id={qtyId}
                            value={field.value}
                            onValueChange={field.onChange}
                            min={0}
                            max={row.soldQty}
                            step={1}
                            disabled={disabled}
                            aria-label={t('cancelar.unidadesDanadas', {
                                defaultValue: 'Unidades dañadas de {{label}}',
                                label: row.label,
                            })}
                            className="w-16"
                        />
                        {fieldState.error?.message ? (
                            <p className={`${FIELD_ERROR} basis-full text-right`}>
                                {fieldState.error.message}
                            </p>
                        ) : null}
                    </div>
                )}
            />
            {row.damagedQty > 0 ? (
                <DamageReasonField
                    control={control}
                    name={`${path}.damageReason`}
                    id={reasonId}
                    label={t('cancelar.motivoDano', {
                        defaultValue: 'Motivo del daño de {{name}}',
                        name: row.label,
                    })}
                    disabled={disabled}
                    t={t}
                />
            ) : null}
        </li>
    );
}

function DamageReasonField({
    control,
    name,
    id,
    label,
    disabled,
    t,
}: {
    control: Control<CancelSaleForm>;
    name: `pieces.${number}.damageReason` | `quantities.${number}.damageReason`;
    id: string;
    label: string;
    disabled: boolean;
    t: Translate;
}) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <div className="space-y-1">
                    <label htmlFor={id} className="sr-only">
                        {label}
                    </label>
                    <Textarea
                        id={id}
                        rows={2}
                        maxLength={CANCEL_REASON_MAX}
                        disabled={disabled}
                        placeholder={t('cancelar.motivoDanoPlaceholder', {
                            defaultValue: '¿Qué daño tiene? Ej.: pantalla estrellada.',
                        })}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                    />
                    {fieldState.error?.message ? (
                        <p className={FIELD_ERROR}>{fieldState.error.message}</p>
                    ) : null}
                </div>
            )}
        />
    );
}

export function CancelSaleModal({
    folio,
    lines,
    onConfirm,
    onClose,
    pending,
    error,
    giftName,
    withoutPhone = false,
}: CancelSaleModalProps) {
    const { t } = useTranslation('ventas');
    const returnable = useMemo(() => returnablePiecesOf(lines, t), [lines, t]);
    const schema = useMemo(() => buildCancelSaleFormSchema(t, { withoutPhone }), [t, withoutPhone]);
    const form = useForm<CancelSaleForm>({
        resolver: zodResolver(schema),
        mode: 'onChange',
        defaultValues: emptyCancelSaleForm(returnable),
    });
    const { control, formState, handleSubmit } = form;
    const reason = useWatch({ control, name: 'reason' });
    const reasonId = useId();
    const equipmentId = useId();
    const errorMessage = cancelErrorMessage(error, t, withoutPhone);
    const hasPieces = returnable.pieces.length > 0 || returnable.quantities.length > 0;

    const submit = handleSubmit((values) => {
        onConfirm(values.reason.trim(), values.equipmentVerified, toReturnConditions(values));
    });

    return (
        <Modal
            isOpen
            onClose={() => {
                if (!pending) onClose();
            }}
            size="md"
            title={
                <span>
                    {t('cancelar.titulo', { defaultValue: 'Cancelar venta' })}{' '}
                    <span className="font-mono text-sm text-muted-foreground">{folio}</span>
                </span>
            }
        >
            <form
                className="space-y-4"
                noValidate
                onSubmit={(e) => {
                    void submit(e);
                }}
            >
                <Banner variant="warning">
                    {t('cancelar.aviso', {
                        defaultValue:
                            'Esta acción es irreversible: la venta queda cancelada y el movimiento se registra en la bitácora con el correo del operador que lo hizo.',
                    })}
                </Banner>

                <div className="space-y-1.5">
                    <label htmlFor={reasonId} className={FIELD_LABEL}>
                        {t('cancelar.motivo', { defaultValue: 'Motivo de la cancelación' })}
                        <span className="ml-0.5 text-destructive">*</span>
                    </label>
                    <Controller
                        control={control}
                        name="reason"
                        render={({ field, fieldState }) => (
                            <>
                                <Textarea
                                    id={reasonId}
                                    value={field.value}
                                    maxLength={CANCEL_REASON_MAX}
                                    disabled={pending}
                                    rows={3}
                                    placeholder={
                                        withoutPhone
                                            ? t('cancelar.motivoPlaceholderPiezas', {
                                                  defaultValue:
                                                      'Ej.: el cliente devolvió las piezas sin abrir.',
                                              })
                                            : t('cancelar.motivoPlaceholder', {
                                                  defaultValue:
                                                      'Ej.: el cliente devolvió el equipo sin abrir.',
                                              })
                                    }
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                />
                                {field.value.length > 0 && fieldState.error?.message ? (
                                    <p className={FIELD_ERROR}>{fieldState.error.message}</p>
                                ) : null}
                            </>
                        )}
                    />
                    <p className="text-right text-xs tabular-nums text-muted-foreground">
                        {reason.length}/{CANCEL_REASON_MAX}
                    </p>
                </div>

                {hasPieces ? (
                    <section className="space-y-1">
                        <h3 className={FIELD_LABEL}>
                            {t('cancelar.condicionTitulo', {
                                defaultValue: 'Condición de lo que regresa',
                            })}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                            {t('cancelar.condicionAyuda', {
                                defaultValue:
                                    'Lo que marques como dañado no vuelve a estar disponible para vender. Todo lo demás regresa en buen estado.',
                            })}
                        </p>
                        <ul className="divide-y divide-border">
                            {returnable.pieces.map((piece, index) => (
                                <PieceConditionRow
                                    key={`${piece.sku}-${piece.identifier}`}
                                    control={control}
                                    index={index}
                                    source={piece}
                                    giftName={giftName}
                                    disabled={pending}
                                    t={t}
                                />
                            ))}
                            {returnable.quantities.map((row, index) => (
                                <QuantityConditionRow
                                    key={row.sku}
                                    control={control}
                                    index={index}
                                    source={row}
                                    giftName={giftName}
                                    disabled={pending}
                                    t={t}
                                />
                            ))}
                        </ul>
                    </section>
                ) : null}

                <div className="flex items-start gap-2">
                    <Controller
                        control={control}
                        name="equipmentVerified"
                        render={({ field }) => (
                            <Checkbox
                                id={equipmentId}
                                checked={field.value}
                                disabled={pending}
                                className="mt-0.5"
                                onCheckedChange={(checked) => {
                                    field.onChange(checked === true);
                                }}
                            />
                        )}
                    />
                    <label htmlFor={equipmentId} className="text-sm leading-snug text-foreground">
                        {withoutPhone
                            ? t('cancelar.piezasConfirmadas', {
                                  defaultValue: 'Confirmo que revisé las piezas que se devuelven.',
                              })
                            : t('cancelar.equipoVerificado', {
                                  defaultValue:
                                      'Confirmo que el equipo fue verificado en tienda y está en condiciones de devolución.',
                              })}
                    </label>
                </div>

                {errorMessage ? <Banner variant="error">{errorMessage}</Banner> : null}

                <div className="flex justify-end gap-2 pt-1">
                    <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
                        {t('cancelar.volver', { defaultValue: 'Volver' })}
                    </Button>
                    <Button
                        type="submit"
                        variant="destructive"
                        disabled={!formState.isValid}
                        isLoading={pending}
                    >
                        {t('cancelar.confirmar', { defaultValue: 'Cancelar venta' })}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
